/**
 * Spec 053 Phase 2 — Stripe payouts and balance (Flow C2 Stripe cards, AC-29b).
 * Shapes mirror billing's records verbatim (`PayoutListResponse`, `PayoutDetailResponse`,
 * `PayoutSyncResponse` under services/billing/.../dto): billing is the source of truth, and the
 * fixtures in the tests next to these types are built from what those records serialise to.
 */

/** `GatewayPayout.PayoutStatus` (pending → in_transit → paid | failed | canceled, upper-cased). */
export type PayoutStatus = 'PENDING' | 'IN_TRANSIT' | 'PAID' | 'FAILED' | 'CANCELED';

export const PAYOUT_STATUSES: PayoutStatus[] = ['PENDING', 'IN_TRANSIT', 'PAID', 'FAILED', 'CANCELED'];

/**
 * `PayoutBalanceResponse` — the latest `gateway_balance_snapshots` row (nightly sweep or "Sync now").
 * Never absent: before the first snapshot billing answers `PayoutBalanceResponse.none()`, an object
 * whose every field is null, so `asOf === null` is the "no snapshot yet" signal. There is no
 * `total` on the wire; the Total card computes available + pending client-side.
 */
export interface PayoutBalanceResponse {
  available: number | null;
  pending: number | null;
  /** ISO-4217, upper-case (`AED`). */
  currency: string | null;
  /** ISO-8601 instant the snapshot was taken. */
  asOf: string | null;
}

/** `PayoutSummaryResponse` — one row on the list and the `payout` block of the detail. */
export interface PayoutSummaryResponse {
  /** Billing's own UUID — the detail route key (`GET /api/admin/payouts/{payoutId}`). */
  payoutId: string;
  /** Stripe `po_…` id — what an admin recognises and what the Stripe dashboard shows. */
  gatewayPayoutId: string;
  /** `YYYY-MM-DD` — Stripe's expected/actual arrival date at the bank. */
  arrivalDate: string | null;
  amount: number;
  currency: string;
  status: PayoutStatus | string;
  automatic: boolean | null;
  /** Balance transactions Stripe lists for this payout; null until the payout is PAID and linked. */
  txnCount: number | null;
  /** Lines matched to a stored payment, refund or dispute; null until linked. */
  matchedCount: number | null;
  /** Money lines with no matching record — AC-29b exceptions; null until linked. */
  exceptionCount: number | null;
  failureReason: string | null;
}

/** `GET /api/admin/payouts` — `PayoutListResponse`. */
export interface PayoutListResponse {
  balance: PayoutBalanceResponse;
  items: PayoutSummaryResponse[];
}

export type PayoutMatchKind = 'PAYMENT' | 'REFUND' | 'DISPUTE';

/** `PayoutMatchedTo` — the local record a line settles. */
export interface PayoutMatchedTo {
  kind: PayoutMatchKind | string;
  /** Billing's payment / refund / dispute UUID. */
  id: string;
  /** Order number when orders could resolve it (`GRG-…`), else null. */
  orderRef: string | null;
}

/**
 * `PayoutLineResponse` — one balance transaction on the payout, signed as Stripe reports it
 * (`net = gross − fee`; refunds and disputes carry negative gross and net).
 */
export interface PayoutLineResponse {
  /** Stripe `txn_…` balance transaction id — unique per payout, the row key. */
  balanceTransactionId: string;
  /** Stripe's lower-case balance transaction type: `charge`, `refund`, `adjustment`, `stripe_fee`, … */
  type: string;
  gross: number;
  fee: number;
  net: number;
  /** Stripe source id (`ch_…`, `re_…`, `dp_…`) or null. */
  sourceRef: string | null;
  /** `null` when unmatched (an AC-29b exception if the line is a money line) or informational. */
  matchedTo: PayoutMatchedTo | null;
}

/**
 * `PayoutReconciliationResponse` — `expected` = Σ net of matched lines, `actual` = payout amount,
 * `variance` = actual − expected. When `available` is false (#540) no line breakdown exists — a manual
 * payout (Stripe exposes no balance transactions for it) or an automatic one whose link is still
 * pending — and `expected` / `variance` / `exceptions` are zero placeholders, not a finding;
 * `unavailableReason` says which. `actual` is always the payout amount.
 */
export interface PayoutReconciliationResponse {
  expected: number;
  actual: number;
  variance: number;
  /** Money lines that match no local record — the AC-29b exception list. */
  exceptions: PayoutLineResponse[];
  available: boolean;
  /** Billing's `PayoutReconciliationService.REASON_*` text; null when `available` is true. */
  unavailableReason: string | null;
}

/** `GET /api/admin/payouts/{payoutId}` — `PayoutDetailResponse`. */
export interface PayoutDetailResponse {
  payout: PayoutSummaryResponse;
  /** Stripe destination (`ba_…`) or null. */
  destination: string | null;
  failureCode: string | null;
  gatewayCreatedAt: string | null;
  paidAt: string | null;
  reconciliation: PayoutReconciliationResponse;
  lines: PayoutLineResponse[];
}

/**
 * `POST /api/admin/payouts/sync` (202) — `PayoutSyncResponse`. `snapshotTakenAt` is null when Stripe
 * could not be reached for the balance (the sweeper never throws); `payoutsSeen` is what the payout
 * list call returned and may still be > 0 in that case.
 */
export interface PayoutSyncResponse {
  payoutsSeen: number;
  snapshotTakenAt: string | null;
}

export interface PayoutListFilters {
  /** `YYYY-MM-DD` inclusive, by arrival date. */
  from?: string;
  to?: string;
  status?: PayoutStatus | '';
}

/**
 * Spec 067 DD4-A — a Tabby payout recorded by hand (`POST /api/admin/finance/provider-payouts`).
 * `cleared` = amountReceived + payoutFee: what left the Tabby receivable (account 1025).
 */
export interface ProviderPayout {
  id: string;
  provider: string;
  amountReceived: number;
  payoutFee: number;
  cleared: number;
  /** `YYYY-MM-DD` — the day the money reached the customer-funds bank. */
  receivedOn: string;
  reference: string;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ProviderPayoutRequest {
  provider: 'TABBY';
  amountReceived: number;
  payoutFee: number;
  receivedOn: string;
  reference: string;
  notes?: string;
}

/** `GET /api/admin/finance/provider-payouts?provider=TABBY` — the latest 50 plus the form's default fee. */
export interface ProviderPayoutListResponse {
  provider: string;
  defaultPayoutFee: number;
  currency: string;
  payouts: ProviderPayout[];
}
